import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL } from "../lib/siteUrl";

const SITE = SITE_URL;

/**
 * SEO wrapper using react-helmet-async.
 * Injects per-page title, description, canonical, OG/Twitter, and optional JSON-LD.
 *
 * Enhancements:
 *  - `ogType` configurable ("website" default, "article" for blog posts)
 *  - `image` defaults to og-image.jpg with width/height/alt metadata
 *  - `articleMeta` for blog posts → emits article:published_time / modified_time
 */
export default function Seo({
    title,
    description,
    canonical,
    keywords,
    image = `${SITE}/og-image.jpg`,
    imageAlt = "Electrocase – Réparateur électroménager à domicile",
    jsonLd,
    noindex = false,
    ogType = "website",
    articleMeta = null, // { publishedTime, modifiedTime, section, tag }
    preloadHero = null, // slug of hero image to preload (e.g. "hero-laundry") — LCP optimization
}) {
    const location = useLocation();
    const url = canonical || `${SITE}${location.pathname}`;

    useEffect(() => {
        // Scroll to top on route change for clean entry into new pages
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <Helmet>
            <html lang="fr" />
            <title>{title.length > 65 && title.endsWith(" | Electrocase") ? title.slice(0, -14) : title}</title>
            <meta name="description" content={description} />
            {/* `keywords` meta intentionally NOT rendered: ignored by Google since 2009,
                may leak SEO strategy to competitors. Caller may still pass `keywords` for
                future use or analytics segmentation. */}
            <link rel="canonical" href={url} />
            <meta name="robots" content={noindex ? "noindex, follow" : "max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />

            {/* LCP hero preload : discover the image before HTML body parsing completes.
                imagesrcset lets the browser pick the correct viewport-appropriate variant
                (sm on mobile → 25KB, md on tablet, lg on desktop). Massive LCP win. */}
            {preloadHero && (
                <link
                    rel="preload"
                    as="image"
                    href={`/img/${preloadHero}-md.webp`}
                    imagesrcset={`/img/${preloadHero}-sm.webp 400w, /img/${preloadHero}-md.webp 800w, /img/${preloadHero}-lg.webp 1200w`}
                    imagesizes="(max-width: 768px) 400px, (max-width: 1200px) 800px, 1200px"
                    fetchpriority="high"
                    type="image/webp"
                />
            )}

            {/* Open Graph */}
            <meta property="og:type" content={ogType} />
            <meta property="og:site_name" content="Electrocase" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:locale" content="fr_FR" />
            <meta property="og:image" content={image} />
            <meta property="og:image:secure_url" content={image} />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={imageAlt} />

            {/* Article meta (blog posts) */}
            {articleMeta && articleMeta.publishedTime && (
                <meta property="article:published_time" content={articleMeta.publishedTime} />
            )}
            {articleMeta && articleMeta.modifiedTime && (
                <meta property="article:modified_time" content={articleMeta.modifiedTime} />
            )}
            {articleMeta && articleMeta.section && (
                <meta property="article:section" content={articleMeta.section} />
            )}
            {articleMeta && articleMeta.tag && (
                <meta property="article:tag" content={articleMeta.tag} />
            )}

            {/* Twitter Cards */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:image:alt" content={imageAlt} />

            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
}
